// Dev-only fixture + dispatcher for the Agents page's change history —
// `GET /v1/agents/history?agent=<id>` (#5208, #5209 client: `lib/agents-history.ts`).
// `null` means "not mine" and `mockFetch` falls through.
//
// Typed against the domain's own wire (`AgentHistoryEventWire`) so a fixture
// that drifts from the server fails here too, the same discipline
// `agent-activity-mocks.ts` follows for its own read.
//
// One member's edit + one PAT-driven edit, so `ChangedByChip` has both a real
// avatar/login row and a `via: 'pat'` row to render its channel glyph from;
// every agent answers the SAME two rows (stateless, like `agentActivityMock`) —
// the chip is per-agent addressed but this fixture does not need to vary by id
// to prove the popover opens and lists real rows.

import type { AgentHistoryEventWire } from '@developerz/domain';
import { at, me } from './mock-session';
import type { Page } from './paged-types';

const rows: readonly AgentHistoryEventWire[] = [
  {
    id: 'aud_agent_2',
    event: 'llm_route.set',
    at_iso: at('2026-09-20'),
    scope: 'org',
    scope_ref: me.active_account_id,
    purpose: 'general',
    mode_key: null,
    before: { key_label: null, provider: null, model: null, reasoning_effort: null },
    after: {
      key_label: null,
      provider: 'anthropic',
      model: 'claude-opus-5-5',
      reasoning_effort: null,
    },
    enabled: null,
    via: 'session',
    actor: { user_id: me.user.id, login: me.user.login, avatar_url: me.user.avatar },
    token_id: null,
  },
  {
    id: 'aud_agent_1',
    event: 'llm_route.cleared',
    at_iso: at('2026-09-12'),
    scope: 'org',
    scope_ref: me.active_account_id,
    purpose: 'general',
    mode_key: null,
    before: { key_label: 'kimi-coding', provider: null, model: null, reasoning_effort: null },
    after: null,
    enabled: null,
    via: 'pat',
    actor: null,
    token_id: 'tok_ci_1',
  },
];

/** Serve the Agents page's history read; `null` for anything else. */
export function agentsHistoryMock(path: string, method: string): Promise<unknown> | null {
  const [bare] = path.split('?');
  if (bare !== '/v1/agents/history' || method !== 'GET') return null;
  return Promise.resolve({ data: rows, next_cursor: null } satisfies Page<AgentHistoryEventWire>);
}
